<script lang="ts" setup>
import type {
  JobFilter,
  JobFilterComparison,
  JobFilterCustomDataProperty,
  JobFilterProperty,
  JobFilterValue,
} from '@ironsight/types'
import { useRouter } from 'vue-router/composables'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import JobFiltersHeader from '@/components/jobs/job-search-page/JobFiltersHeader.vue'
import JobFiltersChipList from '@/components/jobs/job-search-page/JobFiltersChipList.vue'
import { useJobListStore } from '@/components/jobs/job-search-page/jobListStore'
import VsText from '@/components/vision/VsText.vue'
import { VAutocomplete } from 'vuetify/lib/components'
import { useKeydownEventListener } from '@/common/useKeydownEventListener'
import { getDefaultValueForType } from '@/components/jobs/job-search-page/useJobFilterProperties'
import {
  type AutocompleteSuggestion,
  highlightMatches,
  useFilterAutocomplete,
} from '@/components/jobs/job-search-page/useFilterAutocomplete'
import { useJobNumberExists } from '@/components/jobs/job-search-page/useJobNumberExists'
import { useAutocompleteFilterAdapter } from '@/components/jobs/job-search-page/useAutocompleteFilterAdapter'
import { TelemetryEvent, useTelemetry } from '@/common/useTelemetry'
import {
  buildFilterSearchMenuOptions,
  createMenuDivider,
  findFirstSelectableMenuOption,
} from '@/components/jobs/job-search-page/filterSearchMenuOptions'
import { useFilteredStore } from '@/components/filters/useFilteredStore'
import { useFilterProperties } from '@/components/filters/useFilterProperties'
import {
  FiltersTopBarEntity,
  useFilterContextProvider,
} from '@/components/filters/useFilterContextBridge'
import { useJobSearchRouteContext } from '@/common/useJobSearchRouteContext'
import { LOCATION_FILTER_IDS } from '@/components/jobs/job-search-page/useFilterChips'

export type MenuOption = {
  text: string
  html?: string
  value: string
  icon?: string
  onClick: () => void
  divider?: boolean
  header?: string
}

const props = withDefaults(
  defineProps<{
    isFetching: boolean
    isMobileView: boolean
    dataUpdatedAt: number
    refreshError?: boolean
    entityType?: FiltersTopBarEntity
    enableAutoComplete?: boolean
    enableJobNumberSuggestion?: boolean
    enableJobFiltersHeader?: boolean
  }>(),
  {
    entityType: 'Job',
    enableAutoComplete: true,
    enableJobNumberSuggestion: true,
    enableJobFiltersHeader: true,
  },
)

const emit = defineEmits<{
  (e: 'refresh'): void
}>()

const JOB_NUMBER_OPTION_PREFIX = 'job-number-search:'
const CUSTOM_DATA_FIELD_TYPE = 'CustomData'
const PLACEHOLDER_TEXT = 'Start typing to filter by...'

const filterSearchValue = ref('')
const filterSearchInput = ref<string | null>('')
const filterInputElement = ref<InstanceType<typeof VAutocomplete> | null>(null)
const desktopMenuItemTooltipVisible = ref<Record<string, boolean>>({})
const isMobileMenuOpen = ref(false)
const mobileFilterText = ref<string | null>('')

let previousBodyOverflow: string | null = null

const openMobileMenu = () => {
  isMobileMenuOpen.value = true
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
}

const router = useRouter()
const { detailRouteName } = useJobSearchRouteContext()
const jobListStore = useJobListStore()

const resolvedStore = useFilteredStore(props.entityType)
useFilterContextProvider({ store: resolvedStore, entityType: props.entityType })

const {
  removeFilter,
  setFilter,
  setPendingFilter,
  getAllFilters,
  getVisibleFilters,
  pendingFilter,
} = resolvedStore
const autocompleteMenuMaxHeight = computed(() => `${Math.min(800, 0.8 * window.innerHeight)}px`)

const { basicFilterableProperties, customFilterableProperties } = useFilterProperties(
  props.entityType,
)

const autoCompleteEnabled = computed(() => props.enableAutoComplete)
const { trackEvent } = useTelemetry()

const { suggestions: autocompleteSuggestions, clear: clearAutocompleteSuggestions } =
  useFilterAutocomplete(filterSearchInput, autoCompleteEnabled)

const filterableProperties = computed(() => {
  const basic = basicFilterableProperties.value || []
  const custom = customFilterableProperties.value || []

  return [...basic, ...custom]
})

const createJobNumberMenuOption = (jobNumber: string): MenuOption => ({
  text: `Open Job #${jobNumber}`,
  value: `${JOB_NUMBER_OPTION_PREFIX}${jobNumber}`,
  onClick: () => {
    router.push({
      name: detailRouteName.value,
      params: { id: jobNumber },
    })
  },
})

const desktopJobNumber = computed(() => {
  const query = (filterSearchInput.value ?? '').trim()
  return query && !isNaN(Number(query)) ? query : null
})
const { exists: desktopJobExists } = useJobNumberExists(
  desktopJobNumber,
  props.enableJobNumberSuggestion,
)

const menuOptions = computed<MenuOption[]>(() => {
  let options = basicFilterableProperties.value
  options = options.filter((prop) => prop.visibilityType !== 'Hidden')
  const standardOptions = options.map((prop) => ({
    text: prop.label,
    value: prop.label,
    icon: prop.icon,
    onClick() {
      handleSelectFilter(prop)
    },
  }))

  let customOptions: MenuOption[] = []
  if (customFilterableProperties.value && customFilterableProperties.value.length > 0) {
    customOptions = [
      createMenuDivider<MenuOption>(),
      ...customFilterableProperties.value
        .filter((prop) => prop.visibilityType !== 'Hidden')
        .map((prop) => ({
          text: prop.label,
          value: prop.key,
          icon: prop.icon,
          onClick() {
            handleSelectCustomDataFilter(prop)
          },
        })),
    ]
  }

  return [...standardOptions, ...customOptions]
})

const desktopJobNumberOption = computed<MenuOption | null>(() => {
  const query = desktopJobNumber.value
  if (query && desktopJobExists.value && props.enableJobNumberSuggestion) {
    return createJobNumberMenuOption(query)
  }
  return null
})

const {
  resolve: resolveAutocompleteValue,
  createFilterValue: createAutocompleteFilterValue,
  appendFilterValue: appendAutocompleteFilterValue,
} = useAutocompleteFilterAdapter()

const resolveAutocompleteProperty = (
  suggestion: AutocompleteSuggestion,
): JobFilterProperty | undefined => {
  if (suggestion.fieldType === CUSTOM_DATA_FIELD_TYPE) {
    return customFilterableProperties.value.find(
      (p) => p.customDataKey === suggestion.customDataKey,
    )
  }

  return filterableProperties.value.find((p) => p.name === suggestion.filterProperty)
}

const getAutocompleteFilterPropertyName = (
  property: JobFilterProperty,
  suggestion: AutocompleteSuggestion,
) => (isCustomDataProperty(property) ? property.name : suggestion.filterProperty)

const getAutocompleteSuggestionGroupKey = (suggestion: AutocompleteSuggestion) =>
  suggestion.fieldType === CUSTOM_DATA_FIELD_TYPE
    ? `${CUSTOM_DATA_FIELD_TYPE}:${suggestion.customDataKey ?? suggestion.valueId}`
    : suggestion.fieldType

const handleSelectAutocompleteSuggestion = (
  suggestion: AutocompleteSuggestion,
  searchText: string,
) => {
  const property = resolveAutocompleteProperty(suggestion)
  if (!property) return

  const filterProperty = getAutocompleteFilterPropertyName(property, suggestion)
  const isCustomDataAutocomplete = isCustomDataProperty(property)

  trackEvent(
    TelemetryEvent.JobFilterAutocompleteSuggestionClicked,
    {
      filterProperty: isCustomDataAutocomplete ? property.key : filterProperty,
      displayValue: suggestion.displayValue,
      searchText,
    },
    {},
  )

  const resolvedValue = isCustomDataAutocomplete
    ? suggestion.displayValue
    : resolveAutocompleteValue(filterProperty, suggestion.valueId, suggestion.displayValue)

  const existingFilter = filtersForPropertyLookup.value.find(
    (f) => f.property === filterProperty && (!isCustomDataAutocomplete || f.key === property.key),
  )

  if (existingFilter) {
    const currentValue = existingFilter.value
    if (Array.isArray(currentValue)) {
      const { nextValue, alreadyHas } = appendAutocompleteFilterValue(
        filterProperty,
        currentValue as JobFilterValue[],
        suggestion.displayValue,
        resolvedValue,
      )

      if (!alreadyHas) {
        setFilter({
          ...existingFilter,
          hidden: false,
          value: nextValue,
        })
      } else {
        if (existingFilter.hidden) {
          setFilter({ ...existingFilter, hidden: false })
        }
      }
    } else {
      setFilter({
        ...existingFilter,
        hidden: false,
        value: resolvedValue,
      })
    }
    emit('refresh')
  } else {
    const isLookup = property.filterType === 'StringLookup' || property.filterType === 'IntLookup'
    const newFilter: JobFilter = {
      id: isCustomDataAutocomplete ? createCustomDataChipId(property.key) : crypto.randomUUID(),
      property: filterProperty,
      type: property.filterType,
      comparison: property.supportedComparisons[0],
      value: isLookup
        ? createAutocompleteFilterValue(filterProperty, suggestion.displayValue, resolvedValue)
        : resolvedValue,
      ...(isCustomDataAutocomplete
        ? {
            label: property.label,
            key: property.key,
            taskIds: property.taskIds,
            lookupEntity: property.lookupEntity,
            definedOptions: property.definedOptions,
          }
        : {}),
    }
    setFilter(newFilter)
    emit('refresh')
  }

  clearAutocompleteSuggestions()
}

const mapSuggestionsToMenuOptions = (
  items: AutocompleteSuggestion[],
  query?: string | null,
): MenuOption[] => {
  if (items.length === 0) return []

  // Cache resolved properties so we don't re-run .find() per suggestion multiple times
  const resolvedCache = new Map<AutocompleteSuggestion, JobFilterProperty>()

  const groups = new Map<string, AutocompleteSuggestion[]>()
  for (const s of items) {
    const resolved = resolveAutocompleteProperty(s)
    if (s.fieldType === CUSTOM_DATA_FIELD_TYPE && !resolved) {
      continue
    }
    if (resolved) {
      resolvedCache.set(s, resolved)
    }

    const groupKey = getAutocompleteSuggestionGroupKey(s)
    if (!groups.has(groupKey)) {
      groups.set(groupKey, [])
    }
    const group = groups.get(groupKey)
    if (group) {
      group.push(s)
    }
  }

  const result: MenuOption[] = []
  for (const [, groupItems] of groups) {
    const headerProperty = resolvedCache.get(groupItems[0])
    result.push({
      header: headerProperty?.label ?? groupItems[0].fieldLabel,
    } as unknown as MenuOption)
    for (const s of groupItems) {
      const property = resolvedCache.get(s)
      result.push({
        text: s.displayValue,
        html: highlightMatches(s.displayValue, query ?? ''),
        value: `autocomplete:${s.fieldType}:${s.customDataKey ?? ''}:${s.valueId}:${s.displayValue}`,
        icon: property?.icon ?? s.icon,
        onClick: () => handleSelectAutocompleteSuggestion(s, query ?? ''),
      })
    }
  }

  return result
}

const autocompleteSuggestionMenuOptions = computed<MenuOption[]>(() =>
  mapSuggestionsToMenuOptions(autocompleteSuggestions.value, filterSearchInput.value),
)

const desktopAutocompleteItems = computed<MenuOption[]>(() => {
  return buildFilterSearchMenuOptions<MenuOption>({
    query: filterSearchInput.value ?? '',
    menuOptions: menuOptions.value,
    autocompleteSuggestions: autocompleteSuggestionMenuOptions.value,
    jobNumberOption: desktopJobNumberOption.value,
    includeAutocompleteSuggestions: autoCompleteEnabled.value,
  })
})

const isDesktopMenuItemTooltipVisibleFor = (item: MenuOption) =>
  !!desktopMenuItemTooltipVisible.value[item.value]

const handleDesktopMenuItemMouseEnter = (item: MenuOption, target: EventTarget | null) => {
  const currentTarget = target as HTMLElement | null
  const textElement = currentTarget?.querySelector('.filter-by-menu-item') as HTMLElement | null

  if (!textElement) {
    return
  }

  const isOverflowing = textElement.scrollWidth > textElement.clientWidth

  desktopMenuItemTooltipVisible.value = {
    ...desktopMenuItemTooltipVisible.value,
    [item.value]: isOverflowing,
  }
}

const handleDesktopMenuItemMouseLeave = (item: MenuOption) => {
  desktopMenuItemTooltipVisible.value = {
    ...desktopMenuItemTooltipVisible.value,
    [item.value]: false,
  }
}

const clearDesktopMenuItemTooltips = () => {
  desktopMenuItemTooltipVisible.value = {}
}

const mobileJobNumber = computed(() => {
  const query = (mobileFilterText.value ?? '').trim()
  return query && !isNaN(Number(query)) ? query : null
})
const { exists: mobileJobExists } = useJobNumberExists(
  mobileJobNumber,
  props.enableJobNumberSuggestion,
)

const mobileJobNumberOption = computed<MenuOption | null>(() => {
  const query = mobileJobNumber.value
  if (query && mobileJobExists.value && props.enableJobNumberSuggestion) {
    return createJobNumberMenuOption(query)
  }
  return null
})

const { suggestions: mobileAutocompleteSuggestions, clear: clearMobileAutocompleteSuggestions } =
  useFilterAutocomplete(mobileFilterText, autoCompleteEnabled)

const mobileAutocompleteSuggestionMenuOptions = computed<MenuOption[]>(() =>
  mapSuggestionsToMenuOptions(mobileAutocompleteSuggestions.value, mobileFilterText.value),
)

const mobileFilteredOptions = computed<MenuOption[]>(() => {
  return buildFilterSearchMenuOptions<MenuOption>({
    query: mobileFilterText.value ?? '',
    menuOptions: menuOptions.value,
    autocompleteSuggestions: mobileAutocompleteSuggestionMenuOptions.value,
    jobNumberOption: mobileJobNumberOption.value,
    includeAutocompleteSuggestions: autoCompleteEnabled.value,
  })
})

const firstMobileSelectableOption = computed<MenuOption | null>(() =>
  findFirstSelectableMenuOption(mobileFilteredOptions.value),
)

const selectMobileOption = (option: MenuOption) => {
  if (option && option.onClick) {
    option.onClick()
  } else {
    return
  }
  nextTick(() => {
    isMobileMenuOpen.value = false
    mobileFilterText.value = ''
    clearMobileAutocompleteSuggestions()
  })
}

const mobileAppendIcon = computed(() => (mobileFilterText.value ? 'close' : 'mdi-filter-outline'))

const handleAppendIcon = () => {
  if (mobileFilterText.value) {
    mobileFilterText.value = ''
  }
}

watch(isMobileMenuOpen, (isOpen) => {
  if (isOpen) {
    previousBodyOverflow = document.body.style.overflow || null
    document.body.style.overflow = 'hidden'
  } else {
    if (previousBodyOverflow !== null) {
      document.body.style.overflow = previousBodyOverflow
    } else {
      document.body.style.overflow = ''
    }
  }
})

onBeforeUnmount(() => {
  document.body.style.overflow = previousBodyOverflow || ''
})

const createCustomDataChipId = (key: string) => `custom_data_${key.replace(/[^A-Za-z0-9_-]/g, '_')}`

const handleSelectCustomDataFilter = (customProperty: JobFilterCustomDataProperty) => {
  if (!customProperty) {
    return
  }

  openChip(createCustomDataChipId(customProperty.key), customProperty)
}

const filtersForPropertyLookup = computed(() => getAllFilters.value)

const getVisibleFilter = (id: string) => getVisibleFilters.value.find((filter) => filter.id === id)

const openChip = (id: string, property?: JobFilterProperty) => {
  const currentFilter = getVisibleFilter(id)
  if (currentFilter) {
    setPendingFilter({
      ...currentFilter,
      value: Array.isArray(currentFilter.value) ? [...currentFilter.value] : currentFilter.value,
    })
  } else {
    if (!property) {
      return
    }

    const defaultValue = getDefaultValueForType(property.filterType)

    let newFilter: JobFilter
    if (isCustomDataProperty(property)) {
      newFilter = {
        id,
        property: 'CustomData',
        type: property.filterType,
        comparison: property.supportedComparisons[0],
        value: defaultValue,
        label: property.label,
        key: property.key,
        taskIds: property.taskIds,
        lookupEntity: property.lookupEntity,
        definedOptions: property.definedOptions,
      }
    } else {
      newFilter = {
        id,
        property: property.name,
        type: property.filterType,
        comparison: property.supportedComparisons[0],
        value: defaultValue,
      }
    }

    setPendingFilter(newFilter)
  }
}

const isCustomDataProperty = (
  property: JobFilterProperty,
): property is JobFilterCustomDataProperty => {
  return (property as JobFilterCustomDataProperty).key !== undefined
}

const closeExpandedChip = () => {
  setPendingFilter(undefined)
}

const toggleChip = (id: string) => {
  if (pendingFilter.value?.id === id) {
    closeExpandedChip()
  } else {
    openChip(id)
  }
}

const handleSelectFilter = (property: JobFilterProperty) => {
  if (!property) {
    return
  }

  const existingFilter = filtersForPropertyLookup.value.find(
    (chip) => chip.property === property.name,
  )

  if (existingFilter) {
    if (existingFilter.hidden) {
      setFilter({ ...existingFilter, hidden: false })
    }
    // Always ensure we open the chip when selected from dropdown
    // First close any open chip
    closeExpandedChip()
    // Then open this one on next tick
    nextTick(() => {
      openChip(existingFilter.id)
    })
    return
  }

  openChip(crypto.randomUUID(), property)
}

const handleOperationChange = async (value: JobFilterComparison) => {
  if (value && pendingFilter.value) {
    setPendingFilter({
      ...pendingFilter.value,
      comparison: value,
    })
  }
}

const removeFilterChip = async (id: string) => {
  removeFilter(id)
  if (pendingFilter.value?.id === id) {
    closeExpandedChip()
  }
  emit('refresh')
}

const applyPendingChanges = async () => {
  if (pendingFilter.value) {
    const chipId = pendingFilter.value.id
    const pendingValue = pendingFilter.value.value
    const pendingComparison = pendingFilter.value.comparison
    const filter = getVisibleFilter(chipId)

    if (!filter) {
      return
    }
    if (pendingComparison) {
      filter.comparison = pendingComparison
    }

    const isLocationFilter = LOCATION_FILTER_IDS.includes(filter.property)
    const isEmpty = Array.isArray(pendingValue)
      ? pendingValue.length === 0 ||
        (!isLocationFilter && (pendingValue as JobFilterValue[]).every((v) => v === null))
      : pendingValue === ''

    // Remove chip if value is considered empty (and operation isn't 'IsEmpty')
    if (isEmpty && filter.comparison !== 'IsEmpty') {
      removeFilter(chipId)
      closeExpandedChip()
      return
    }

    if (pendingValue !== undefined) {
      filter.value = pendingValue
      setFilter(filter)
    }

    closeExpandedChip()
    emit('refresh')
  }
}

const updateInputValue = (value: JobFilterValue) => {
  if (pendingFilter.value) {
    setPendingFilter({
      ...pendingFilter.value,
      value,
    })
  }
}

const focusFilterInput = () => {
  if (filterInputElement.value) {
    const element = filterInputElement.value.$el.querySelector('input')
    if (element) {
      element.click()
    }
  }
}

const openVisibleFilterChipByIndex = (index: number) => {
  const filter = getVisibleFilters.value[index]
  if (!filter) {
    return
  }

  openChip(filter.id)
}

const chipOpenShortcutHandlers = Array.from({ length: 9 }, (_, index) => {
  const shortcutNumber = String(index + 1)

  return {
    [`cmd+shift+${shortcutNumber}`]: () => {
      openVisibleFilterChipByIndex(index)
    },
    [`ctrl+shift+${shortcutNumber}`]: () => {
      openVisibleFilterChipByIndex(index)
    },
  }
}).reduce<Record<string, () => void>>((acc, handlers) => Object.assign(acc, handlers), {})

watch(filterSearchValue, (newValue) => {
  if (newValue) {
    const selectedOption = desktopAutocompleteItems.value.find(
      (option) => option.value === newValue,
    )
    if (selectedOption && selectedOption.onClick) {
      selectedOption.onClick()
      nextTick().then(() => {
        clearDesktopMenuItemTooltips()
        filterSearchValue.value = ''
      })
    }
  }
})

useKeydownEventListener({
  f: () => {
    focusFilterInput()
  },
  ...chipOpenShortcutHandlers,
})

useKeydownEventListener(
  {
    'cmd+enter': () => {
      if (pendingFilter.value) {
        applyPendingChanges()
      }
    },
    'ctrl+enter': () => {
      if (pendingFilter.value) {
        applyPendingChanges()
      }
    },
  },
  {
    disableWhileTypingInFormControls: false,
    capture: true,
    stopPropagation: true,
  },
)
</script>

<template>
  <div>
    <div class="mt-1 white">
      <div v-if="enableJobFiltersHeader">
        <job-filters-header
          :is-fetching="isFetching"
          :is-mobile-view="isMobileView"
          :last-fetch-time="dataUpdatedAt"
          :selected-jobs="jobListStore.selectedJobs"
          :refresh-error="refreshError"
          class="px-4"
          @refresh="emit('refresh')"
          @clear-selection="jobListStore.updateSelectedJobs([])"
          @open-bulk-update="jobListStore.updateIsBulkUpdateFormOpen(true)"
        />
        <v-divider class="mt-1"></v-divider>
      </div>
      <template v-if="isMobileView">
        <v-row no-gutters class="px-4 my-3" align="stretch">
          <v-col cols="12">
            <div class="mobile-input-search flex-fill">
              <v-text-field
                v-model="mobileFilterText"
                append-icon="mdi-filter-outline"
                outlined
                hide-details
                :label="PLACEHOLDER_TEXT"
                class="autocomplete"
                rounded
                @focus="openMobileMenu()"
                @click="openMobileMenu()"
              />
            </div>
          </v-col>
          <template v-if="isMobileMenuOpen">
            <v-col cols="12">
              <transition name="mobile-search-fade">
                <div class="mobile-menu-overlay" @click="isMobileMenuOpen = false"></div>
              </transition>
              <transition name="mobile-search-panel">
                <div
                  class="mobile-menu-panel"
                  role="dialog"
                  aria-modal="true"
                  :aria-label="PLACEHOLDER_TEXT"
                  @keydown.esc.stop.prevent="closeMobileMenu()"
                >
                  <div class="mobile-menu-header">
                    <v-icon class="mr-2" aria-label="Back" role="button" @click="closeMobileMenu()"
                      >arrow_back</v-icon
                    >
                    <v-text-field
                      v-model="mobileFilterText"
                      :append-outer-icon="mobileAppendIcon"
                      solo
                      flat
                      hide-details
                      :placeholder="PLACEHOLDER_TEXT"
                      class="mobile-search-input mobile-search-input--bar"
                      autofocus
                      @click:append-outer="handleAppendIcon()"
                      @keyup.enter="
                        firstMobileSelectableOption &&
                        selectMobileOption(firstMobileSelectableOption)
                      "
                    />
                  </div>
                  <div class="mobile-menu-list">
                    <v-list>
                      <template v-if="mobileFilteredOptions.length">
                        <template v-for="(opt, idx) in mobileFilteredOptions">
                          <v-divider v-if="opt && opt.divider" :key="`d-${idx}`" class="my-1" />
                          <v-subheader v-else-if="opt && opt.header" :key="`h-${idx}`">
                            {{ opt.header }}
                          </v-subheader>
                          <v-list-item v-else :key="`i-${idx}`" @click="selectMobileOption(opt)">
                            <v-list-item-content>
                              <div class="d-flex align-center" style="min-width: 0">
                                <v-icon v-if="opt.icon" class="mr-3 material-icons-outlined">{{
                                  opt.icon
                                }}</v-icon>
                                <vs-text
                                  v-if="opt.html"
                                  class="filter-by-menu-item"
                                  v-html="opt.html"
                                />
                                <vs-text v-else class="filter-by-menu-item">{{ opt.text }}</vs-text>
                              </div>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </template>
                      <template v-else>
                        <div class="pa-4">
                          <vs-text center type="subdued"
                            >No suggestions found — try filtering by a specific field</vs-text
                          >
                        </div>
                      </template>
                    </v-list>
                  </div>
                </div>
              </transition>
            </v-col>
          </template>
          <v-col cols="12">
            <job-filters-chip-list
              :expanded-chip-id="pendingFilter?.id"
              :filterable-properties="filterableProperties"
              class="mt-2"
              @toggle-chip="toggleChip"
              @remove-chip="removeFilterChip"
              @operation-change="handleOperationChange"
              @update-value="updateInputValue"
              @apply-filter="applyPendingChanges"
            />
          </v-col>
        </v-row>
      </template>
      <template v-else>
        <v-row no-gutters class="px-4 my-3 flex-nowrap" align="center">
          <div class="input-search fixed-360">
            <v-autocomplete
              ref="filterInputElement"
              v-model="filterSearchValue"
              :items="desktopAutocompleteItems"
              :search-input.sync="filterSearchInput"
              :menu-props="{
                rounded: 'xl',
                maxHeight: autocompleteMenuMaxHeight,
                nudgeBottom: 4,
                contentClass: 'filter-by-menu',
                attach: 'body',
                minWidth: 420,
                maxWidth: 540,
              }"
              :filter="
                autoCompleteEnabled && (filterSearchInput || '').trim() ? () => true : undefined
              "
              auto-select-first
              bottom
              eager
              hide-details
              outlined
              :label="PLACEHOLDER_TEXT"
              class="autocomplete"
              rounded
              @blur="clearDesktopMenuItemTooltips"
            >
              <template #item="{ item }">
                <v-tooltip bottom :value="isDesktopMenuItemTooltipVisibleFor(item)">
                  <template #activator="{ attrs }">
                    <div
                      class="d-flex align-center"
                      style="min-width: 0"
                      v-bind="attrs"
                      @mouseenter="handleDesktopMenuItemMouseEnter(item, $event.currentTarget)"
                      @mouseleave="handleDesktopMenuItemMouseLeave(item)"
                    >
                      <v-icon v-if="item.icon" class="mr-3 material-icons-outlined"
                        >{{ item.icon }}
                      </v-icon>
                      <vs-text v-if="item.html" class="filter-by-menu-item" v-html="item.html" />
                      <vs-text v-else class="filter-by-menu-item">{{ item.text }}</vs-text>
                    </div>
                  </template>
                  <span>{{ item.text }}</span>
                </v-tooltip>
              </template>
              <template #append>
                <v-icon>mdi-filter-outline</v-icon>
              </template>
              <template #no-data>
                <div style="width: 100%">
                  <vs-text center type="subdued"
                    >No suggestions found — try filtering by a specific field</vs-text
                  >
                </div>
              </template>
            </v-autocomplete>
          </div>
          <job-filters-chip-list
            :expanded-chip-id="pendingFilter?.id"
            :filterable-properties="filterableProperties"
            class="px-2 flex-fill"
            @toggle-chip="toggleChip"
            @remove-chip="removeFilterChip"
            @operation-change="handleOperationChange"
            @update-value="updateInputValue"
            @apply-filter="applyPendingChanges"
          />
          <slot name="trailing" />
        </v-row>
      </template>
    </div>
    <v-divider />
  </div>
</template>

<style scoped>
.input-search {
  max-width: 300px;
  margin-left: 8px;
  margin-right: 8px;
}

.fixed-360 {
  flex: 0 0 360px;
}

.flex-fill {
  flex: 1 1 auto;
  min-width: 0;
}

.input-search :deep(.v-input--is-focused .v-label) {
  color: var(--color-grey--dark) !important;
}

.filter-by-menu-item {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-by-menu-item :deep(strong) {
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.mobile-input-search {
  max-width: 300px;
  margin-right: 8px;
}

.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 3999;
}

.mobile-menu-panel {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: var(--color-white);
  z-index: 4000;
  display: flex;
  flex-direction: column;
}

.mobile-menu-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  height: 56px;
  border-bottom: 1px solid var(--color-grey--lighter);
}

.mobile-menu-list {
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  flex: 1 1 auto;
}

.mobile-menu-list :deep(.v-list-item .v-list-item__content) {
  padding: 0 16px;
}

.mobile-search-input {
  flex: 1 1 auto;
}

.mobile-search-input :deep(.v-label--active),
.autocomplete :deep(.v-label--active) {
  transform: translateY(-24px) scale(0.75) !important;
}

.mobile-search-input--bar :deep(.v-input__slot) {
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
}
.mobile-search-input--bar :deep(.v-input__slot::before),
.mobile-search-input--bar :deep(.v-input__slot::after) {
  display: none !important;
}
</style>
